import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowsLeftRight, CalendarBlank, CaretLeft, CaretRight, Check, GearSix, Plus, X } from "@phosphor-icons/react";
import { useDepartments } from "../components/DepartmentPicker";

const SHIFT_PALETTE = [
  "bg-amber-50 text-amber-700 border border-amber-200",
  "bg-sky-50 text-sky-700 border border-sky-200",
  "bg-indigo-50 text-indigo-700 border border-indigo-200",
  "bg-slate-900 text-white",
  "bg-emerald-50 text-emerald-700 border border-emerald-200",
  "bg-rose-50 text-rose-700 border border-rose-200",
];
const shiftStyle = (key, types) => {
  if (key === "off") return "bg-slate-100 text-slate-400";
  const i = types.findIndex((t) => t.key === key);
  return SHIFT_PALETTE[(i >= 0 ? i : 0) % SHIFT_PALETTE.length];
};

function mondayOf(d) {
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7;
  dt.setDate(dt.getDate() - day);
  return dt.toISOString().slice(0, 10);
}

export default function RosterPage() {
  const { user } = useAuth();
  const canManage = ["admin", "manager"].includes(user.role);
  const [start, setStart] = useState(mondayOf(new Date()));
  const [data, setData] = useState(null);
  const [deptFilter, setDeptFilter] = useState("");
  const [memberQ, setMemberQ] = useState("");
  const [showShiftMgr, setShowShiftMgr] = useState(false);
  const [types, setTypes] = useState([]);
  const [swaps, setSwaps] = useState([]);
  const [showSwap, setShowSwap] = useState(false);
  const [swapDate, setSwapDate] = useState("");
  const [swapTargetDate, setSwapTargetDate] = useState("");
  const [swapTarget, setSwapTarget] = useState("");
  const [swapNote, setSwapNote] = useState("");
  const departments = useDepartments();
  const scoped = user.role === "manager" && (user.department_ids || []).length > 0 && !user.is_master_operator;

  const load = () => api.get("/roster", { params: { start, department_id: deptFilter } })
    .then((r) => { setData(r.data); setTypes(r.data.shift_types || []); })
    .catch((e) => toast.error(apiError(e)));
  const loadSwaps = () => api.get("/roster/swaps").then((r) => setSwaps(r.data)).catch(() => {});
  useEffect(() => { load(); }, [start, deptFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadSwaps(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const shiftFor = (uid, day) => data.entries.find((e) => e.user_id === uid && e.date === day)?.shift || "";

  const labelOf = (key) => types.find((t) => t.key === key)?.label || key || "—";

  const cycle = async (uid, day) => {
    if (!canManage) {
      if (uid !== user.id) return;
      const cur = shiftFor(uid, day);
      if (!cur || cur === "off") return toast("No shift on that day — nothing to swap");
      setSwapDate(day); setSwapTargetDate(day); setSwapTarget(""); setSwapNote(""); setShowSwap(true);
      return;
    }
    const opts = data.shift_options;
    const cur = shiftFor(uid, day);
    const next = opts[(opts.indexOf(cur) + 1) % opts.length] || opts[0];
    try {
      const { data: entry } = await api.post("/roster", { user_id: uid, date: day, shift: next });
      setData((d) => ({ ...d, entries: [...d.entries.filter((e) => !(e.user_id === uid && e.date === day)), entry] }));
    } catch (e) { toast.error(apiError(e)); }
  };

  const copyLastWeek = async () => {
    const prev = new Date(start);
    prev.setDate(prev.getDate() - 7);
    try {
      const { data: res } = await api.post("/roster/copy-week", { from_start: prev.toISOString().slice(0, 10), to_start: start });
      toast.success(res.copied > 0 ? `Copied ${res.copied} shift${res.copied > 1 ? "s" : ""} from last week` : "Last week has no shifts to copy");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitSwap = async () => {
    if (!swapTarget) return toast.error("Pick a colleague to swap with");
    try {
      await api.post("/roster/swaps", { date: swapDate, target_user_id: swapTarget, target_date: swapTargetDate || swapDate, note: swapNote });
      toast.success("Swap request sent — your manager will be notified");
      setShowSwap(false); loadSwaps();
    } catch (e) { toast.error(apiError(e)); }
  };

  const decideSwap = async (id, action) => {
    try {
      await api.put(`/roster/swaps/${id}`, { action });
      toast.success(action === "approve" ? "Swap approved — roster updated & both notified" : "Swap rejected");
      loadSwaps(); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const shiftWeek = (delta) => {
    const dt = new Date(start);
    dt.setDate(dt.getDate() + delta * 7);
    setStart(dt.toISOString().slice(0, 10));
  };

  if (!data) return <p className="text-slate-400">Loading roster…</p>;

  return (
    <div className="space-y-8 rise-in" data-testid="roster-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400">Shift Planning</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
            <CalendarBlank size={36} weight="duotone" className="text-emerald-600" /> Duty Roster
          </h1>
          <p className="text-slate-500 mt-3">{canManage ? "Tap a cell to cycle shifts. Swap requests from staff appear below for one-tap approval." : "Your team's weekly shift schedule — tap one of your own shifts to request a swap."}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {canManage && (
            <button data-testid="roster-shift-settings" onClick={() => setShowShiftMgr(true)}
              className="h-11 px-4 rounded-full border border-slate-200 text-xs font-bold text-slate-600 flex items-center gap-1.5 tap-press hover:border-slate-900">
              <GearSix size={16} /> Shifts
            </button>
          )}
          {canManage && (
            <button data-testid="roster-copy-week" onClick={copyLastWeek}
              className="h-11 px-5 rounded-full border border-slate-200 text-xs font-bold text-slate-600 tap-press hover:border-slate-900">
              Copy last week
            </button>
          )}
          <button data-testid="roster-prev-week" onClick={() => shiftWeek(-1)} className="h-11 w-11 rounded-full border border-slate-200 flex items-center justify-center tap-press hover:border-slate-900"><CaretLeft size={18} /></button>
          <span className="text-sm font-bold px-3" data-testid="roster-week-label">Week of {start}</span>
          <button data-testid="roster-next-week" onClick={() => shiftWeek(1)} className="h-11 w-11 rounded-full border border-slate-200 flex items-center justify-center tap-press hover:border-slate-900"><CaretRight size={18} /></button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        {!scoped && departments.length > 0 && (
          <select data-testid="roster-dept-filter" value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}
            className="h-11 px-4 rounded-xl border border-slate-200 bg-white font-semibold text-sm">
            <option value="">All departments</option>
            {departments.filter((d) => d.active !== false).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        )}
        <input data-testid="roster-member-filter" value={memberQ} onChange={(e) => setMemberQ(e.target.value)}
          placeholder="Filter by team member name…" className="flex-1 h-11 px-4 rounded-xl border border-slate-200 text-sm" />
      </div>

      {(canManage ? swaps.filter((s) => s.status === "pending") : swaps).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid="swap-panel">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-3 flex items-center gap-1.5">
            <ArrowsLeftRight size={16} /> {canManage ? "Pending swap requests" : "My swap requests"}
          </p>
          <div className="space-y-2.5">
            {(canManage ? swaps.filter((s) => s.status === "pending") : swaps.slice(0, 6)).map((s) => (
              <div key={s.id} data-testid={`swap-row-${s.id}`} className="flex flex-wrap items-center justify-between gap-3 border border-slate-100 rounded-xl px-4 py-3">
                <div className="text-sm">
                  <p className="font-bold">
                    {s.requester_name} <span className="text-slate-400 font-semibold">({s.date} · {labelOf(s.requester_shift)})</span>
                    <ArrowsLeftRight size={14} className="inline mx-2 text-slate-400" />
                    {s.target_user_name} <span className="text-slate-400 font-semibold">({s.target_date} · {labelOf(s.target_shift)})</span>
                  </p>
                  {s.note && <p className="text-xs text-slate-500 mt-0.5">"{s.note}"</p>}
                </div>
                {canManage && s.status === "pending" ? (
                  <div className="flex gap-2">
                    <button data-testid={`swap-approve-${s.id}`} onClick={() => decideSwap(s.id, "approve")}
                      className="h-10 px-4 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center gap-1.5 tap-press hover:bg-emerald-700">
                      <Check size={14} /> Approve
                    </button>
                    <button data-testid={`swap-reject-${s.id}`} onClick={() => decideSwap(s.id, "reject")}
                      className="h-10 px-4 rounded-full border border-red-200 text-red-600 text-xs font-bold flex items-center gap-1.5 tap-press hover:bg-red-50">
                      <X size={14} /> Reject
                    </button>
                  </div>
                ) : (
                  <span data-testid={`swap-status-${s.id}`} className={`text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-full ${
                    s.status === "pending" ? "bg-amber-50 text-amber-700 border border-amber-200"
                      : s.status === "approved" ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-red-50 text-red-600 border border-red-200"}`}>
                    {s.status}{s.decided_by ? ` · ${s.decided_by}` : ""}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="roster-grid">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left px-5 py-4 text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400">Team member</th>
              {data.days.map((d) => (
                <th key={d} className="px-2 py-4 text-[11px] uppercase tracking-wider font-bold text-slate-400 text-center">
                  {new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" })}<br />
                  <span className="text-slate-300 normal-case">{d.slice(5)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.staff.filter((s) => !memberQ || s.name.toLowerCase().includes(memberQ.toLowerCase())).map((s) => (
              <tr key={s.id} className="border-b border-slate-50 last:border-0" data-testid={`roster-row-${s.id}`}>
                <td className="px-5 py-3">
                  <p className="font-bold">{s.name}</p>
                  <p className="text-xs text-slate-400">{s.position}</p>
                </td>
                {data.days.map((d) => {
                  const sh = shiftFor(s.id, d);
                  const t = types.find((x) => x.key === sh);
                  return (
                    <td key={d} className="px-1.5 py-2 text-center">
                      <button data-testid={`roster-cell-${s.id}-${d}`} onClick={() => cycle(s.id, d)}
                        disabled={!canManage && s.id !== user.id}
                        title={t && t.start ? `${t.label} ${t.start}–${t.end}` : t?.label || ""}
                        className={`w-full h-10 rounded-lg text-[11px] font-bold leading-tight tap-press ${sh ? shiftStyle(sh, types) : "bg-slate-50 text-slate-300 border border-dashed border-slate-200"} ${canManage || s.id === user.id ? "cursor-pointer" : "cursor-default"}`}>
                        {t ? t.label.replace(" Shift", "") : sh || "—"}
                        {t && t.start && <span className="block text-[9px] font-semibold opacity-70">{t.start}–{t.end}</span>}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {data.staff.length === 0 && <p className="text-slate-400 p-6">No team members yet.</p>}
      </div>

      {showShiftMgr && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => setShowShiftMgr(false)}>
          <div data-testid="shift-manager-modal" onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl p-6 sm:p-8 rise-in">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-2xl font-bold">Property shift types</h2>
              <button onClick={() => setShowShiftMgr(false)} className="p-2 rounded-full hover:bg-slate-100 tap-press"><X size={22} /></button>
            </div>
            <div className="space-y-3">
              {types.map((t, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`shift-type-row-${i}`}>
                  <input value={t.label} onChange={(e) => setTypes(types.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                    placeholder="Shift name" className="flex-1 h-11 px-3 rounded-lg border border-slate-200 text-sm font-semibold" />
                  <input value={t.start} onChange={(e) => setTypes(types.map((x, j) => j === i ? { ...x, start: e.target.value } : x))}
                    placeholder="07:00" className="w-20 h-11 px-2 rounded-lg border border-slate-200 text-sm text-center" />
                  <input value={t.end} onChange={(e) => setTypes(types.map((x, j) => j === i ? { ...x, end: e.target.value } : x))}
                    placeholder="15:30" className="w-20 h-11 px-2 rounded-lg border border-slate-200 text-sm text-center" />
                  {t.key !== "off" && (
                    <button data-testid={`shift-type-remove-${i}`} onClick={() => setTypes(types.filter((_, j) => j !== i))}
                      className="p-2 text-slate-300 hover:text-red-500 tap-press"><X size={16} /></button>
                  )}
                </div>
              ))}
              <button data-testid="shift-type-add" onClick={() => setTypes([...types, { key: "", label: "", start: "", end: "" }])}
                className="h-11 px-5 rounded-full border border-slate-200 text-xs font-bold flex items-center gap-1.5 tap-press hover:border-slate-900">
                <Plus size={14} /> Add shift type
              </button>
              <button data-testid="shift-types-save" onClick={async () => {
                try {
                  const { data: res } = await api.put("/roster/shift-types", { shift_types: types });
                  setTypes(res.shift_types);
                  toast.success("Shift types saved — dropdowns updated across the property");
                  setShowShiftMgr(false); load();
                } catch (e) { toast.error(apiError(e)); }
              }} className="w-full h-13 py-3.5 rounded-full bg-slate-900 text-white font-heading font-bold tap-press hover:bg-slate-800">
                Save shift configuration
              </button>
            </div>
          </div>
        </div>
      )}
      {showSwap && data && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => setShowSwap(false)}>
          <div data-testid="swap-request-modal" onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-md rounded-3xl p-6 sm:p-8 rise-in space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl font-bold">Request shift swap</h2>
              <button onClick={() => setShowSwap(false)} className="p-2 rounded-full hover:bg-slate-100 tap-press"><X size={22} /></button>
            </div>
            <p className="text-sm text-slate-500">Your shift: <span className="font-bold text-slate-900">{swapDate} · {labelOf(shiftFor(user.id, swapDate))}</span></p>
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mb-1.5">Swap with</p>
              <select data-testid="swap-colleague-select" value={swapTarget} onChange={(e) => setSwapTarget(e.target.value)}
                className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold text-sm">
                <option value="">Choose a colleague…</option>
                {data.staff.filter((m) => m.id !== user.id).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mb-1.5">Their shift date</p>
              <select data-testid="swap-date-select" value={swapTargetDate} onChange={(e) => setSwapTargetDate(e.target.value)}
                className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold text-sm">
                {data.days.map((d) => <option key={d} value={d}>{d}{swapTarget ? ` · ${labelOf(shiftFor(swapTarget, d))}` : ""}</option>)}
              </select>
            </div>
            <input data-testid="swap-note-input" value={swapNote} onChange={(e) => setSwapNote(e.target.value)}
              placeholder="Reason (optional)" className="w-full h-12 px-4 rounded-xl border border-slate-200 text-sm" />
            <button data-testid="swap-submit" onClick={submitSwap}
              className="w-full py-3.5 rounded-full bg-slate-900 text-white font-heading font-bold tap-press hover:bg-slate-800">
              Send swap request
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
